import { create } from "zustand";
import {
  getConversations,
  getMessages,
  sendMessage as sendMessageAPI,
  createDirectConversation,
  createTeamConversation,
} from "../api/messages";
import useAuthStore from "./authStore";

// Create notification sound using Web Audio API
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Create a pleasant notification sound (two tone chime)
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      audioContext.currentTime + 0.3
    );

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (error) {
    console.error("Error playing notification sound:", error);
  }
};

const useChatStore = create((set, get) => ({
  conversations: [],
  messages: {},
  selectedConversationId: null,
  loading: false,
  socket: null,

  // Set socket instance for real-time updates
  setSocket: (socket) => {
    const state = get();

    // Clean up old socket listeners if any
    if (state.socket) {
      state.socket.off("message:created");
    }

    // Set up new socket listeners
    if (socket) {
      socket.on("message:created", (message) => {
        // Get current user from auth store
        const currentUser = useAuthStore.getState().user;
        const currentUserId = currentUser?._id || currentUser?.id;
        const messageSenderId =
          message.senderId._id || message.senderId.id || message.senderId;
        const isSentByMe = String(messageSenderId) === String(currentUserId);

        // Add the new message to the appropriate conversation
        // Deduplicate by message ID to prevent duplicate keys in React
        const msgConvId = String(message.conversationId);
        set((state) => {
          const existingMessages = state.messages[msgConvId] || [];
          const messageId = String(message._id);

          // Check if message already exists
          const messageExists = existingMessages.some(
            (msg) => String(msg._id) === messageId
          );

          // Only add if it doesn't exist
          if (messageExists) {
            return state;
          }

          return {
            messages: {
              ...state.messages,
              [msgConvId]: [...existingMessages, message],
            },
          };
        });

        // Update conversation lastMessageAt
        set((state) => ({
          conversations: state.conversations.map((conv) => {
            // Convert both IDs to strings for comparison
            const convId = String(conv.id);
            const msgConvId = String(message.conversationId);

            if (convId === msgConvId) {
              const selectedId = String(state.selectedConversationId || "");
              const isForSelectedConv = selectedId === msgConvId;

              // Play notification sound for messages from others in non-selected conversations
              if (!isSentByMe && !isForSelectedConv) {
                playNotificationSound();

                // Fetch conversations to get updated unread count from backend
                const token = useAuthStore.getState().token;
                if (token) {
                  // Fetch in background - don't await
                  get()
                    .fetchConversations(token)
                    .catch((err) =>
                      console.error(
                        "Error fetching conversations after new message:",
                        err
                      )
                    );
                }
              }

              return {
                ...conv,
                lastMessageAt: new Date(),
                // Unread count will be updated when fetchConversations completes
              };
            }
            return conv;
          }),
        }));
      });
    }

    set({ socket });
  },

  // Fetch all conversations
  fetchConversations: async (token) => {
    set({ loading: true });
    try {
      const conversations = await getConversations(token);
      set({ conversations, loading: false });
      return conversations;
    } catch (error) {
      console.error("Error fetching conversations:", error);
      set({ loading: false });
      return [];
    }
  },

  // Fetch messages for a conversation
  fetchMessages: async (conversationId, token) => {
    set({ loading: true });
    try {
      const messages = await getMessages(conversationId, token);
      set((state) => ({
        messages: {
          ...state.messages,
          [conversationId]: messages,
        },
        loading: false,
      }));
      return messages;
    } catch (error) {
      console.error("Error fetching messages:", error);
      set({ loading: false });
      return [];
    }
  },

  // Send a message
  sendMessage: async (conversationId, text, token) => {
    try {
      const newMessage = await sendMessageAPI(conversationId, text, token);

      // Don't add message to local state here - the socket event will handle it
      // This prevents duplicate messages when the server emits 'message:created'

      return newMessage;
    } catch (error) {
      console.error("Error sending message:", error);
      throw error;
    }
  },

  // Get or create a direct conversation
  getOrCreateDirectConversation: async (otherUserId, token) => {
    try {
      const conversation = await createDirectConversation(otherUserId, token);

      // Add to conversations if not already there
      set((state) => {
        const exists = state.conversations.some(
          (conv) => conv.id === conversation._id
        );
        if (!exists) {
          return {
            conversations: [
              ...state.conversations,
              {
                id: conversation._id,
                type: conversation.type,
                participants: conversation.participants,
                lastMessageAt: conversation.lastMessageAt,
                unread: 0,
              },
            ],
          };
        }
        return state;
      });

      return conversation;
    } catch (error) {
      console.error("Error creating direct conversation:", error);
      throw error;
    }
  },

  // Get or create a team conversation
  getOrCreateTeamConversation: async (teamId, token) => {
    try {
      const conversation = await createTeamConversation(teamId, token);

      // Add to conversations if not already there
      set((state) => {
        const exists = state.conversations.some(
          (conv) => conv.id === conversation._id
        );
        if (!exists) {
          return {
            conversations: [
              ...state.conversations,
              {
                id: conversation._id,
                type: conversation.type,
                team: conversation.teamId,
                lastMessageAt: conversation.lastMessageAt,
                unread: 0,
              },
            ],
          };
        }
        return state;
      });

      return conversation;
    } catch (error) {
      console.error("Error creating team conversation:", error);
      throw error;
    }
  },

  // Set selected conversation
  setSelectedConversation: (conversationId) => {
    set({ selectedConversationId: conversationId });
  },

  // Mark conversation as read (reset unread count)
  markAsRead: (conversationId) => {
    set((state) => ({
      conversations: state.conversations.map((conv) =>
        conv.id === conversationId ? { ...conv, unread: 0 } : conv
      ),
    }));
  },

  // Add a new message (for socket events)
  addMessage: (conversationId, message) => {
    set((state) => ({
      messages: {
        ...state.messages,
        [conversationId]: [...(state.messages[conversationId] || []), message],
      },
    }));
  },

  // Clean up orphaned conversations (for teams/members that no longer exist)
  cleanupOrphanedConversations: (validTeamIds, validMemberIds) => {
    // Don't cleanup if we have no valid IDs - this would remove everything!
    if (validTeamIds.length === 0 && validMemberIds.length === 0) {
      return;
    }

    set((state) => ({
      conversations: state.conversations.filter((conv) => {
        if (conv.type === "team") {
          return (
            validTeamIds.length > 0 && validTeamIds.includes(conv.team?._id)
          );
        } else if (conv.type === "direct") {
          return conv.participants?.some((p) => {
            // Handle both populated objects and raw ObjectIds
            const participantId =
              typeof p === "string" ? p : p._id || p.id || String(p);
            return validMemberIds.includes(participantId);
          });
        }
        return false;
      }),
    }));
  },

  // Get total unread count across all conversations
  getTotalUnreadCount: () => {
    const state = get();
    return state.conversations.reduce(
      (total, conv) => total + (conv.unread || 0),
      0
    );
  },

  // Clear all chat data (call on logout)
  clearChatData: () => {
    const state = get();

    // Clean up socket listeners
    if (state.socket) {
      state.socket.off("message:created");
    }

    set({
      conversations: [],
      messages: {},
      selectedConversationId: null,
      socket: null,
    });
  },
}));

export default useChatStore;
